import { isElementInLockedLayer, isLayerLocked, isElementEffectivelyLocked } from './layer-tools';
/**
 * Tools for event.
 * @module event
 * @license MIT
 * @copyright 2011 Jeff Schiller
 */
import {
    assignAttributes, cleanupElement, getElem, getRotationAngle, snapToGrid, walkTree,
    getBBox as utilsGetBBox, isNullish, preventClickDefault, setHref
} from './utilities';
import {
    convertAttrs
} from '../common/units';
import {
    transformPoint, hasMatrixTransform, getMatrix, snapToAngle, XYObject
} from './math';
import {
    getTransformList
} from './svgtransformlist';
import {
    supportsNonScalingStroke, isWebkit
} from '../common/browser';
import * as draw from './draw';
import * as pathModule from './path';
import { findPos } from '../editor/jgraduate/Util.js';
import { SvgAnnotations } from '../components';
import { InsertElementCommand } from './undocommands';
import SvgCanvas from './svgcanvas';
import { zenDataServiceInstance } from '../services';

let svgCanvas: SvgCanvas = null;
let eventContext_ = null;
let moveSelectionThresholdReached = false;

/**
* @function module:undo.init
* @param {module:undo.eventContext} eventContext
* @returns {void}
*/
export const init = function (_svgCanvas: SvgCanvas, eventContext) {
    svgCanvas = _svgCanvas;
    eventContext_ = eventContext;
};

const getBsplinePoint = function (t: number): XYObject {
    const spline = { x: 0, y: 0 },
        p0 = { x: eventContext_.getControllPoint2('x'), y: eventContext_.getControllPoint2('y') },
        p1 = { x: eventContext_.getControllPoint1('x'), y: eventContext_.getControllPoint1('y') },
        p2 = { x: eventContext_.getStart('x'), y: eventContext_.getStart('y') },
        p3 = { x: eventContext_.getEnd('x'), y: eventContext_.getEnd('y') },
        S = 1.0 / 6.0,
        t2 = t * t,
        t3 = t2 * t;

    const m = [
        [-1, 3, -3, 1],
        [3, -6, 3, 0],
        [-3, 0, 3, 0],
        [1, 4, 1, 0]
    ];

    spline.x = S * (
        (p0.x * m[0][0] + p1.x * m[0][1] + p2.x * m[0][2] + p3.x * m[0][3]) * t3 +
        (p0.x * m[1][0] + p1.x * m[1][1] + p2.x * m[1][2] + p3.x * m[1][3]) * t2 +
        (p0.x * m[2][0] + p1.x * m[2][1] + p2.x * m[2][2] + p3.x * m[2][3]) * t +
        (p0.x * m[3][0] + p1.x * m[3][1] + p2.x * m[3][2] + p3.x * m[3][3])
    );
    spline.y = S * (
        (p0.y * m[0][0] + p1.y * m[0][1] + p2.y * m[0][2] + p3.y * m[0][3]) * t3 +
        (p0.y * m[1][0] + p1.y * m[1][1] + p2.y * m[1][2] + p3.y * m[1][3]) * t2 +
        (p0.y * m[2][0] + p1.y * m[2][1] + p2.y * m[2][2] + p3.y * m[2][3]) * t +
        (p0.y * m[3][0] + p1.y * m[3][1] + p2.y * m[3][2] + p3.y * m[3][3])
    );

    return {
        x: spline.x,
        y: spline.y
    };
};

// update the dummy transform in our transform list
// to be a translate. We need to check if there was a transformation
// to avoid loosing it
const updateTransformList = (svgRoot: SVGSVGElement, element: SVGGraphicsElement, dx: number, dy: number) => {
    const tlist = getTransformList(element);

    if (tlist.numberOfItems) {
        const firstItem = tlist.getItem(0);
        if (firstItem.type === 2) { // SVG_TRANSFORM_TRANSLATE = 2
            // If we already have a transform element, set it directly. It's twice as fast as
            // creating a new transform and insertItemBefore-ing it.
            firstItem.setTranslate(dx, dy);
            return;
        }
    }

    const xform = svgRoot.createSVGTransform();
    xform.setTranslate(dx, dy);

    if (tlist.numberOfItems) {
        tlist.insertItemBefore(xform, 0);
    } else {
        tlist.appendItem(xform);
    }
}

/**
 *
 * @param {MouseEvent} evt
 * @fires module:svgcanvas.SvgCanvas#event:transition
 * @fires module:svgcanvas.SvgCanvas#event:ext_mouseMove
 * @returns {void}
 */
export const mouseMoveEvent = function (evt: MouseEvent) {
    const selectedElements = svgCanvas.getSelectedElements();
    const currentZoom = svgCanvas.getCurrentZoom();
    const svgRoot = svgCanvas.getRootElem();

    if (!svgCanvas.inputState.getStarted()) {
        return;
    }

    if (evt.button === 1 || svgCanvas.inputState.spaceKeyDown) {
        return;
    }

    let i;
    let xya;
    let cx;
    let cy;
    let dx;
    let dy;
    let len;
    let angle;
    let box;
    const selected = selectedElements[0];
    const pt = transformPoint(evt.pageX, evt.pageY, svgCanvas.getRootSctm());

    const mouseX = pt.x * currentZoom;
    const mouseY = pt.y * currentZoom;
    const shape = getElem(svgCanvas.getId());

    let realX = mouseX / currentZoom;
    let x = realX;
    let realY = mouseY / currentZoom;
    let y = realY;

    if (svgCanvas.getCurrentConfig().gridSnapping) {
        x = snapToGrid(x);
        y = snapToGrid(y);
    }

    evt.preventDefault();
    let tlist;
    switch (svgCanvas.getCurrentMode()) {
        case 'select': {
            // we temporarily use a translate on the element(s) being dragged
            // this transform is removed upon mousing up and the element is
            // relocated to the new location
            if (selected) {
                dx = x - eventContext_.getStartX();
                dy = y - eventContext_.getStartY();
                if (svgCanvas.getCurrentConfig().gridSnapping) {
                    dx = snapToGrid(dx);
                    dy = snapToGrid(dy);
                }

                // Hold Shift to constrain the move to a single axis (Illustrator-style):
                // lock to whichever axis currently has the larger offset.
                if (evt.shiftKey) {
                    if (Math.abs(dx) > Math.abs(dy)) {
                        dy = 0;
                    } else {
                        dx = 0;
                    }
                }

                // Enable moving selection only if mouse has been moved at least 4 px in any direction
                // This prevents objects from being accidentally moved when (initially) selected
                const deltaThreshold = 4 / currentZoom + 0.0001;
                const deltaThresholdReached = Math.abs(dx) > deltaThreshold || Math.abs(dy) > deltaThreshold;
                moveSelectionThresholdReached = moveSelectionThresholdReached || deltaThresholdReached;

                if (moveSelectionThresholdReached) {
                    const unlockedSelectedElements = selectedElements.filter(el => el && !isElementEffectivelyLocked(el));
                    selectedElements.forEach((el) => {
                        if (el && !isElementEffectivelyLocked(el)) {
                            updateTransformList(svgRoot, el, dx, dy)
                            // update our internal bbox that we're tracking while dragging
                            svgCanvas.selectorManager.requestSelector(el).resize()
                        }
                    });
                    if (unlockedSelectedElements.length > 0) {
                        svgCanvas.call('transition', unlockedSelectedElements)
                    }
                }
            }
            break;
        } case 'multiselect': {
            realX *= currentZoom;
            realY *= currentZoom;
            assignAttributes(eventContext_.getRubberBox(), {
                x: Math.min(eventContext_.getRStartX(), realX),
                y: Math.min(eventContext_.getRStartY(), realY),
                width: Math.abs(realX - eventContext_.getRStartX()),
                height: Math.abs(realY - eventContext_.getRStartY())
            }, 100);

            // for each selected:
            // - if newList contains selected, do nothing
            // - if newList doesn't contain selected, remove it from selected
            // - for any newList that was not in selectedElements, add it to selected
            const elemsToRemove = selectedElements.slice();
            const elemsToAdd = [];
            const newList = svgCanvas.getIntersectionList();

            // For every element in the intersection, add if not present in selectedElements.
            len = newList.length;
            for (i = 0; i < len; ++i) {
                const intElem = newList[i];
                // Found an element that was not selected before, so we should add it.
                if (!selectedElements.includes(intElem)) {
                    elemsToAdd.push(intElem);
                }
                // Found an element that was already selected, so we shouldn't remove it.
                const foundInd = elemsToRemove.indexOf(intElem);
                if (foundInd !== -1) {
                    elemsToRemove.splice(foundInd, 1);
                }
            }

            if (elemsToRemove.length > 0) {
                svgCanvas.removeFromSelection(elemsToRemove);
            }

            if (elemsToAdd.length > 0) {
                svgCanvas.addToSelection(elemsToAdd);
            }

            break;
        } case 'resize': {
            // we track the resize bounding box and translate/scale the selected element
            // while the mouse is down, when mouse goes up, we use this to recalculate
            // the shape's coordinates
            tlist = getTransformList(selected);
            const hasMatrix = hasMatrixTransform(tlist);
            box = hasMatrix ? eventContext_.getInitBbox() : utilsGetBBox(selected);
            let left = box.x,
                top = box.y,
                { width, height } = box;
            dx = (x - eventContext_.getStartX());
            dy = (y - eventContext_.getStartY());

            if (svgCanvas.getCurrentConfig().gridSnapping) {
                dx = snapToGrid(dx);
                dy = snapToGrid(dy);
                height = snapToGrid(height);
                width = snapToGrid(width);
            }

            // if rotated, adjust the dx,dy values
            angle = getRotationAngle(selected);
            if (angle) {
                const r = Math.sqrt(dx * dx + dy * dy),
                    theta = Math.atan2(dy, dx) - angle * Math.PI / 180.0;
                dx = r * Math.cos(theta);
                dy = r * Math.sin(theta);
            }

            // if not stretching in y direction, set dy to 0
            // if not stretching in x direction, set dx to 0
            if (!eventContext_.getCurrentResizeMode().includes('n') && !eventContext_.getCurrentResizeMode().includes('s')) {
                dy = 0;
            }
            if (!eventContext_.getCurrentResizeMode().includes('e') && !eventContext_.getCurrentResizeMode().includes('w')) {
                dx = 0;
            }

            let // ts = null,
                tx = 0, ty = 0,
                sy = height ? (height + dy) / height : 1,
                sx = width ? (width + dx) / width : 1;
            // if we are dragging on the north side, then adjust the scale factor and ty
            if (eventContext_.getCurrentResizeMode().includes('n')) {
                sy = height ? (height - dy) / height : 1;
                ty = height;
            }

            // if we dragging on the east side, then adjust the scale factor and tx
            if (eventContext_.getCurrentResizeMode().includes('w')) {
                sx = width ? (width - dx) / width : 1;
                tx = width;
            }

            // update the transform list with translate,scale,translate
            const translateOrigin = svgCanvas.getSVGRoot().createSVGTransform(),
                scale = svgCanvas.getSVGRoot().createSVGTransform(),
                translateBack = svgCanvas.getSVGRoot().createSVGTransform();

            if (svgCanvas.getCurrentConfig().gridSnapping) {
                left = snapToGrid(left);
                tx = snapToGrid(tx);
                top = snapToGrid(top);
                ty = snapToGrid(ty);
            }

            translateOrigin.setTranslate(-(left + tx), -(top + ty));
            if (evt.shiftKey) {
                if (sx === 1) {
                    sx = sy;
                } else { sy = sx; }
            }
            scale.setScale(sx, sy);

            translateBack.setTranslate(left + tx, top + ty);
            if (hasMatrix) {
                const diff = angle ? 1 : 0;
                tlist.replaceItem(translateOrigin, 2 + diff);
                tlist.replaceItem(scale, 1 + diff);
                tlist.replaceItem(translateBack, Number(diff));
            } else {
                const N = tlist.numberOfItems;
                tlist.replaceItem(translateBack, N - 3);
                tlist.replaceItem(scale, N - 2);
                tlist.replaceItem(translateOrigin, N - 1);
            }

            svgCanvas.selectorManager.requestSelector(selected).resize();
            svgCanvas.call('transition', selectedElements);

            break;
        } case 'zoom': {
            realX *= currentZoom;
            realY *= currentZoom;
            assignAttributes(eventContext_.getRubberBox(), {
                x: Math.min(eventContext_.getRStartX() * currentZoom, realX),
                y: Math.min(eventContext_.getRStartY() * currentZoom, realY),
                width: Math.abs(realX - eventContext_.getRStartX() * currentZoom),
                height: Math.abs(realY - eventContext_.getRStartY() * currentZoom)
            }, 100);
            break;
        } case 'text': {
            assignAttributes(shape, {
                x,
                y
            }, 1000);
            break;
        } case 'textarea': {
            assignAttributes(shape, {
                transform: `translate(${x}, ${y})`
            }, 1000);
            break;
        } case 'line': {
            if (svgCanvas.getCurrentConfig().gridSnapping) {
                x = snapToGrid(x);
                y = snapToGrid(y);
            }

            let x2 = x;
            let y2 = y;

            if (evt.shiftKey) {
                xya = snapToAngle(eventContext_.getStartX(), eventContext_.getStartY(), x2, y2);
                x2 = xya.x;
                y2 = xya.y;
            }

            shape.setAttribute('x2', x2.toString());
            shape.setAttribute('y2', y2.toString());
            break;
        } case 'foreignObject':
        // fall through
        case 'square':
        // fall through
        case 'rect':
        // fall through
        case 'image': {
            const square = (svgCanvas.getCurrentMode() === 'square') || evt.shiftKey;
            let
                w = Math.abs(x - eventContext_.getStartX()),
                h = Math.abs(y - eventContext_.getStartY());
            let newX, newY;
            if (square) {
                w = h = Math.max(w, h);
                newX = eventContext_.getStartX() < x ? eventContext_.getStartX() : eventContext_.getStartX() - w;
                newY = eventContext_.getStartY() < y ? eventContext_.getStartY() : eventContext_.getStartY() - h;
            } else {
                newX = Math.min(eventContext_.getStartX(), x);
                newY = Math.min(eventContext_.getStartY(), y);
            }

            if (svgCanvas.getCurrentConfig().gridSnapping) {
                w = snapToGrid(w);
                h = snapToGrid(h);
                newX = snapToGrid(newX);
                newY = snapToGrid(newY);
            }

            assignAttributes(shape, {
                width: w,
                height: h,
                x: newX,
                y: newY
            }, 1000);

            break;
        } case 'circle': {
            cx = shape.getAttribute('cx');
            cy = shape.getAttribute('cy');
            let rad = Math.sqrt((x - cx) * (x - cx) + (y - cy) * (y - cy));
            if (svgCanvas.getCurrentConfig().gridSnapping) {
                rad = snapToGrid(rad);
            }
            shape.setAttribute('r', rad.toString());
            break;
        } case 'ellipse': {
            cx = shape.getAttribute('cx');
            cy = shape.getAttribute('cy');
            if (svgCanvas.getCurrentConfig().gridSnapping) {
                x = snapToGrid(x);
                cx = snapToGrid(cx);
                y = snapToGrid(y);
                cy = snapToGrid(cy);
            }
            shape.setAttribute('rx', Math.abs(x - cx).toString());
            const ry = Math.abs(evt.shiftKey ? (x - cx) : (y - cy));
            shape.setAttribute('ry', ry.toString());
            break;
        }
        case 'fhellipse':
        case 'fhrect': {
            eventContext_.setFreehand('minx', Math.min(realX, eventContext_.getFreehand('minx')));
            eventContext_.setFreehand('maxx', Math.max(realX, eventContext_.getFreehand('maxx')));
            eventContext_.setFreehand('miny', Math.min(realY, eventContext_.getFreehand('miny')));
            eventContext_.setFreehand('maxy', Math.max(realY, eventContext_.getFreehand('maxy')));
        }
        // Fallthrough
        case 'fhpath': {
            const polylineShape = shape as SVGPolylineElement;

            // dAttr += + realX + ',' + realY + ' ';
            // shape.setAttribute('points', dAttr);
            eventContext_.setEnd('x', realX);
            eventContext_.setEnd('y', realY);
            if (eventContext_.getControllPoint2('x') && eventContext_.getControllPoint2('y')) {
                for (i = 0; i < eventContext_.getStepCount() - 1; i++) {
                    eventContext_.setParameter(i / eventContext_.getStepCount());
                    eventContext_.setNextParameter((i + 1) / eventContext_.getStepCount());
                    eventContext_.setbSpline(getBsplinePoint(eventContext_.getNextParameter()));
                    eventContext_.setNextPos({ x: eventContext_.getbSpline('x'), y: eventContext_.getbSpline('y') });
                    eventContext_.setbSpline(getBsplinePoint(eventContext_.getParameter()));
                    eventContext_.setSumDistance(
                        eventContext_.getSumDistance() + Math.sqrt((eventContext_.getNextPos('x') -
                            eventContext_.getbSpline('x')) * (eventContext_.getNextPos('x') -
                                eventContext_.getbSpline('x')) + (eventContext_.getNextPos('y') -
                                    eventContext_.getbSpline('y')) * (eventContext_.getNextPos('y') - eventContext_.getbSpline('y')))
                    );
                    if (eventContext_.getSumDistance() > eventContext_.getThreSholdDist()) {
                        eventContext_.setSumDistance(eventContext_.getSumDistance() - eventContext_.getThreSholdDist());

                        // Faster than completely re-writing the points attribute.
                        const point = svgCanvas.getSVGContent().createSVGPoint();
                        point.x = eventContext_.getbSpline('x');
                        point.y = eventContext_.getbSpline('y');
                        polylineShape.points.appendItem(point);
                    }
                }
            }
            eventContext_.setControllPoint2('x', eventContext_.getControllPoint1('x'));
            eventContext_.setControllPoint2('y', eventContext_.getControllPoint1('y'));
            eventContext_.setControllPoint1('x', eventContext_.getStart('x'));
            eventContext_.setControllPoint1('y', eventContext_.getStart('y'));
            eventContext_.setStart({ x: eventContext_.getEnd('x'), y: eventContext_.getEnd('y') });
            break;
            // update path stretch line coordinates
        } case 'path':
        // fall through
        case 'pathedit': {
            x *= currentZoom;
            y *= currentZoom;

            if (svgCanvas.getCurrentConfig().gridSnapping) {
                x = snapToGrid(x);
                y = snapToGrid(y);
                eventContext_.setStartX(snapToGrid(eventContext_.getStartX()));
                eventContext_.setStartY(snapToGrid(eventContext_.getStartY()));
            }
            if (evt.shiftKey) {
                const { path } = pathModule;
                let x1, y1;
                if (path) {
                    x1 = path.dragging ? path.dragging[0] : eventContext_.getStartX();
                    y1 = path.dragging ? path.dragging[1] : eventContext_.getStartY();
                } else {
                    x1 = eventContext_.getStartX();
                    y1 = eventContext_.getStartY();
                }
                xya = snapToAngle(x1, y1, x, y);
                ({ x, y } = xya);
            }

            if (eventContext_.getRubberBox() && eventContext_.getRubberBox().getAttribute('display') !== 'none') {
                realX *= currentZoom;
                realY *= currentZoom;
                assignAttributes(eventContext_.getRubberBox(), {
                    x: Math.min(eventContext_.getRStartX() * currentZoom, realX),
                    y: Math.min(eventContext_.getRStartY() * currentZoom, realY),
                    width: Math.abs(realX - eventContext_.getRStartX() * currentZoom),
                    height: Math.abs(realY - eventContext_.getRStartY() * currentZoom)
                }, 100);
            }
            svgCanvas.pathActions.mouseMove(x, y);

            break;
        } case 'textedit': {
            x *= currentZoom;
            y *= currentZoom;
            // if (eventContext_.getRubberBox() && eventContext_.getRubberBox().getAttribute('display') !== 'none') {
            //   assignAttributes(eventContext_.getRubberBox(), {
            //     x: Math.min(eventContext_.getStartX(), x),
            //     y: Math.min(eventContext_.getStartY(), y),
            //     width: Math.abs(x - eventContext_.getStartX()),
            //     height: Math.abs(y - eventContext_.getStartY())
            //   }, 100);
            // }

            svgCanvas.textActions.mouseMove(mouseX, mouseY);

            break;
        } case 'rotate': {
            box = utilsGetBBox(selected);
            cx = box.x + box.width / 2;
            cy = box.y + box.height / 2;
            const m = getMatrix(selected),
                center = transformPoint(cx, cy, m);
            cx = center.x;
            cy = center.y;
            angle = ((Math.atan2(cy - y, cx - x) * (180 / Math.PI)) - 90) % 360;
            if (svgCanvas.getCurrentConfig().gridSnapping) {
                angle = snapToGrid(angle);
            }
            if (evt.shiftKey) { // restrict rotations to nice angles (WRS)
                const snap = 45;
                angle = Math.round(angle / snap) * snap;
            }

            svgCanvas.setRotationAngle(angle < -180 ? (360 + angle) : angle, true);
            svgCanvas.call('transition', selectedElements);
            break;
        } default:
            break;
    }

    /**
  * The mouse has moved on the canvas area.
  * @event module:svgcanvas.SvgCanvas#event:ext_mouseMove
  * @type {PlainObject}
  * @property {MouseEvent} event The event object
  * @property {Float} mouse_x x coordinate on canvas
  * @property {Float} mouse_y y coordinate on canvas
  * @property {Element} selected Refers to the first selected element
  */
    svgCanvas.runExtensions('mouseMove', /** @type {module:svgcanvas.SvgCanvas#event:ext_mouseMove} */ {
        event: evt,
        mouse_x: mouseX,
        mouse_y: mouseY,
        selected
    });
}; // mouseMove()

// - in create mode, the element's opacity is set properly, we create an InsertElementCommand
// and store it on the Undo stack
// - in move/resize mode, the element's attributes which were affected by the move/resize are
// identified, a ChangeElementCommand is created and stored on the stack for those attrs
// this is done in when we recalculate the selected dimensions()
/**
*
* @param {MouseEvent} evt
* @fires module:svgcanvas.SvgCanvas#event:zoomed
* @fires module:svgcanvas.SvgCanvas#event:changed
* @fires module:svgcanvas.SvgCanvas#event:ext_mouseUp
* @returns {void}
*/
export const mouseUpEvent = function (evt: MouseEvent) {
    moveSelectionThresholdReached = false;

    const selectedElements = svgCanvas.getSelectedElements();
    const currentMode = svgCanvas.getCurrentMode();
    const currentZoom = svgCanvas.getCurrentZoom();
    const rightClick = evt.button === 2;

    if (rightClick) {
        return;
    }
    const tempJustSelected = eventContext_.getJustSelected();
    eventContext_.setJustSelected(null);
    if (!svgCanvas.inputState.getStarted()) {
        return;
    }

    // Need a delay to propagate the changes.
    setTimeout(() => {
        SvgAnnotations.UpdateAnnotations();
    });

    const pt = transformPoint(evt.pageX, evt.pageY, svgCanvas.getRootSctm()),
        mouseX = pt.x * currentZoom,
        mouseY = pt.y * currentZoom,
        x = mouseX / currentZoom,
        y = mouseY / currentZoom;

    let element = getElem<SVGElement>(svgCanvas.getId());
    let keep = false;

    const realX = x;
    const realY = y;

    // TODO: Make true when in multi-unit mode
    const useUnit = false; // (svgCanvas.getCurrentConfig().baseUnit !== 'px');
    svgCanvas.inputState.setStarted(false);
    let t;
    switch (currentMode) {
        // intentionally fall-through to select here
        case 'resize':
        // @ts-expect-error
        case 'multiselect':
            if (!isNullish(eventContext_.getRubberBox())) {
                eventContext_.getRubberBox().setAttribute('display', 'none');
                eventContext_.setCurBBoxes([]);
            }
            svgCanvas.setCurrentMode('select');
        // Fallthrough
        case 'select':
            if (!isNullish(selectedElements[0])) {
                // if we only have one selected element
                if (isNullish(selectedElements[1])) {
                    // set our current stroke/fill properties to the element's
                    const selected = selectedElements[0];
                    const selectedLocked = isElementEffectivelyLocked(selected);
                    switch (selected.tagName) {
                        case 'g':
                        case 'use':
                        case 'image':
                        case 'foreignObject':
                            break;
                        default:
                            eventContext_.setCurProperties('fill', selected.getAttribute('fill'));
                            eventContext_.setCurProperties('fill_opacity', selected.getAttribute('fill-opacity'));
                            eventContext_.setCurProperties('stroke', selected.getAttribute('stroke'));
                            eventContext_.setCurProperties('stroke_opacity', selected.getAttribute('stroke-opacity'));
                            eventContext_.setCurProperties('stroke_width', selected.getAttribute('stroke-width'));
                            eventContext_.setCurProperties('stroke_dasharray', selected.getAttribute('stroke-dasharray'));
                            eventContext_.setCurProperties('stroke_linejoin', selected.getAttribute('stroke-linejoin'));
                            eventContext_.setCurProperties('stroke_linecap', selected.getAttribute('stroke-linecap'));
                    }

                    if (selected.tagName === 'text') {
                        eventContext_.setCurText('font_size', selected.getAttribute('font-size'));
                        eventContext_.setCurText('font_family', selected.getAttribute('font-family'));
                    }
                    svgCanvas.selectorManager.requestSelector(selected).showGrips(!selectedLocked);

                    // This shouldn't be necessary as it was done on mouseDown...
                    //  svgCanvas.call('selected', [selected]);
                }
                // always recalculate dimensions to strip off stray identity transforms
                svgCanvas.recalculateAllSelectedDimensions();
                // if it was being dragged/resized
                if (realX !== eventContext_.getRStartX() || realY !== eventContext_.getRStartY()) {
                    const len = selectedElements.length;
                    for (let i = 0; i < len; ++i) {
                        if (isNullish(selectedElements[i])) { break; }
                        if (!selectedElements[i].firstChild) {
                            // Not needed for groups (incorrectly resizes elems), possibly not needed at all?
                            svgCanvas.selectorManager.requestSelector(selectedElements[i]).resize();
                        }
                    }
                    // no change in position/size, so maybe we should move to pathedit
                } else {
                    t = evt.target;
                    if (selectedElements[0].nodeName === 'path' && isNullish(selectedElements[1]) && !isElementEffectivelyLocked(selectedElements[0])) {
                        svgCanvas.pathActions.select(selectedElements[0]);
                        // if it was a path
                        // else, if it was selected and this is a shift-click, remove it from selection
                    } else if (evt.shiftKey && tempJustSelected !== t) {
                        svgCanvas.removeFromSelection([t]);
                    }
                } // no change in mouse position

                // Remove non-scaling stroke
                if (supportsNonScalingStroke()) {
                    const elem = selectedElements[0];
                    if (elem) {
                        elem.removeAttribute('style');
                        walkTree(elem, function (el) {
                            el.removeAttribute('style');
                        });
                    }
                }
            }
            return;
        case 'zoom': {
            if (!isNullish(eventContext_.getRubberBox())) {
                eventContext_.getRubberBox().setAttribute('display', 'none');
            }
            const factor = evt.shiftKey ? 0.5 : 2;
            svgCanvas.call('zoomed', {
                x: Math.min(eventContext_.getRStartX(), realX),
                y: Math.min(eventContext_.getRStartY(), realY),
                width: Math.abs(realX - eventContext_.getRStartX()),
                height: Math.abs(realY - eventContext_.getRStartY()),
                factor
            });
            return;
        } case 'panning': {
            svgCanvas.editor.setPanning(false);
            break;
        } case 'fhpath': {
            // Check that the path contains at least 2 points; a degenerate one-point path
            // causes problems.
            // Webkit ignores how we set the points attribute with commas and uses space
            // to separate all coordinates, see https://bugs.webkit.org/show_bug.cgi?id=29870
            eventContext_.setSumDistance(0);
            eventContext_.setControllPoint2('x', 0);
            eventContext_.setControllPoint2('y', 0);
            eventContext_.setControllPoint1('x', 0);
            eventContext_.setControllPoint1('y', 0);
            eventContext_.setStart({ x: 0, y: 0 });
            eventContext_.setEnd('x', 0);
            eventContext_.setEnd('y', 0);
            const coords = element.getAttribute('points');
            const commaIndex = coords.indexOf(',');
            keep = commaIndex >= 0 ? coords.includes(',', commaIndex + 1) : coords.includes(' ', coords.indexOf(' ') + 1);
            if (keep) {
                element = svgCanvas.pathActions.smoothPolylineIntoPath(element as SVGPolylineElement);
            }
            break;
        } case 'line': {
            const x1 = element.getAttribute('x1');
            const y1 = element.getAttribute('y1');
            const x2 = element.getAttribute('x2');
            const y2 = element.getAttribute('y2');
            keep = (x1 !== x2 || y1 !== y2);
        }
            break;
        case 'foreignObject':
        case 'square':
        case 'rect':
        case 'image': {
            const width = element.getAttribute('width');
            const height = element.getAttribute('height');
            // Image should be kept regardless of size (use inherit dimensions later)
            keep = typeof (width || height) === 'string' || svgCanvas.getCurrentMode() === 'image';
        }
            break;
        case 'circle':
            keep = (element.getAttribute('r') !== '0');
            break;
        case 'ellipse': {
            const rx = element.getAttribute('rx');
            const ry = element.getAttribute('ry');
            keep = typeof (rx || ry) === 'string';
        }
            break;
        case 'fhellipse':
            if ((eventContext_.getFreehand('maxx') - eventContext_.getFreehand('minx')) > 0 &&
                (eventContext_.getFreehand('maxy') - eventContext_.getFreehand('miny')) > 0) {
                element = svgCanvas.addSVGElementFromJson({
                    element: 'ellipse',
                    curStyles: true,
                    attr: {
                        cx: (eventContext_.getFreehand('minx') + eventContext_.getFreehand('maxx')) / 2,
                        cy: (eventContext_.getFreehand('miny') + eventContext_.getFreehand('maxy')) / 2,
                        rx: (eventContext_.getFreehand('maxx') - eventContext_.getFreehand('minx')) / 2,
                        ry: (eventContext_.getFreehand('maxy') - eventContext_.getFreehand('miny')) / 2,
                        id: svgCanvas.getId(),
                        "pe-fill": "REGISTRATION|100",
                        "pe-stroke": "REGISTRATION|100",
                    }
                });
                svgCanvas.call('changed', [element]);
                keep = true;
            }
            break;
        case 'fhrect':
            if ((eventContext_.getFreehand('maxx') - eventContext_.getFreehand('minx')) > 0 &&
                (eventContext_.getFreehand('maxy') - eventContext_.getFreehand('miny')) > 0) {
                element = svgCanvas.addSVGElementFromJson({
                    element: 'rect',
                    curStyles: true,
                    attr: {
                        x: eventContext_.getFreehand('minx'),
                        y: eventContext_.getFreehand('miny'),
                        width: (eventContext_.getFreehand('maxx') - eventContext_.getFreehand('minx')),
                        height: (eventContext_.getFreehand('maxy') - eventContext_.getFreehand('miny')),
                        id: svgCanvas.getId(),
                        "pe-fill": "REGISTRATION|100",
                        "pe-stroke": "REGISTRATION|100",
                    }
                });
                svgCanvas.call('changed', [element]);
                keep = true;
            }
            break;
        case 'text':
        case 'textarea':
            keep = true;
            svgCanvas.selectOnly([element]);
            if (!isElementEffectivelyLocked(element)) {
                svgCanvas.textActions.start(element as SVGTextElement);
            }
            break;
        case 'path': {
            // set element to null here so that it is not removed nor finalized
            element = null;
            // continue to be set to true so that mouseMove happens
            svgCanvas.inputState.setStarted(true);

            const res = svgCanvas.pathActions.mouseUp(evt, element, mouseX, mouseY);
            ({ element } = res);
            ({ keep } = res);
            break;
        } case 'pathedit':
            keep = true;
            element = null;
            svgCanvas.pathActions.mouseUp(evt);
            break;
        case 'textedit':
            keep = false;
            element = null;
            svgCanvas.textActions.mouseUp(evt, mouseX, mouseY);
            break;
        case 'rotate': {
            keep = true;
            element = null;
            svgCanvas.setCurrentMode('select');
            const batchCmd = svgCanvas.undoMgr.finishUndoableChange();
            if (!batchCmd.isEmpty()) {
                svgCanvas.addCommandToHistory(batchCmd);
            }
            // perform recalculation to weed out any stray identity transforms that might get stuck
            svgCanvas.recalculateAllSelectedDimensions();
            svgCanvas.call('changed', selectedElements);
            break;
        } default:
            // This could occur in an extension
            break;
    }

    /**
      * The main (left) mouse button is released (anywhere).
      * @event module:svgcanvas.SvgCanvas#event:ext_mouseUp
      * @type {PlainObject}
      * @property {MouseEvent} event The event object
      * @property {Float} mouse_x x coordinate on canvas
      * @property {Float} mouse_y y coordinate on canvas
      */
    let extResult = svgCanvas.runExtensions('mouseUp', {
        event: evt,
        mouse_x: mouseX,
        mouse_y: mouseY
    }, true);

    if (!Array.isArray(extResult)) {
        extResult = [extResult];
    }

    extResult.forEach(function (r) {
        if (r) {
            keep = r.keep || keep;
            ({ element } = r);
            svgCanvas.inputState.setStarted(r.started || svgCanvas.inputState.getStarted());
        }
    });

    if (!keep && !isNullish(element)) {
        svgCanvas.getCurrentDrawing().releaseId(svgCanvas.getId());
        element.remove();
        element = null;

        t = evt.target;

        // if this element is in a group, go up until we reach the top-level group
        // just below the layer groups
        // TODO: once we implement links, we also would have to check for <a> elements
        while (t?.parentNode?.parentNode && t.parentNode.parentNode.tagName === 'g') {
            t = t.parentNode;
        }
        // if we are not in the middle of creating a path, and we've clicked on some shape,
        // then go to Select mode.
        // WebKit returns <div> when the canvas is clicked, Firefox/Opera return <svg>
        if ((svgCanvas.getCurrentMode() !== 'path' || !eventContext_.getDrawnPath()) &&
            t?.parentNode &&
            t.parentNode.id !== 'selectorParentGroup' &&
            t.id !== 'svgcanvas' && t.id !== 'svgroot'
        ) {
            // switch into "select" mode if we've clicked on an element
            svgCanvas.setMode('select');
            svgCanvas.selectOnly([t], true);
        }
    } else if (!isNullish(element)) {
        if (useUnit) {
            convertAttrs(element);
        }

        let aniDur = 0.2;
        let cAni;
        const curShape = svgCanvas.getStyle();
        const opacAni = eventContext_.getOpacAni();
        if (opacAni.beginElement && Number.parseFloat(element.getAttribute('opacity')) !== curShape.opacity) {
            cAni = opacAni.cloneNode(true);
            cAni.setAttribute('to', curShape.opacity);
            cAni.setAttribute('dur', aniDur);
            element.appendChild(cAni);
            try {
                // Fails in FF4 on foreignObject
                cAni.beginElement();
            } catch (e) {/* empty fn */ }
        } else {
            aniDur = 0;
        }

        // Ideally this would be done on the endEvent of the animation,
        // but that doesn't seem to be supported in Webkit
        setTimeout(function () {
            if (cAni) {
                cAni.remove();
            }
            element.setAttribute('opacity', curShape.opacity.toString());
            element.setAttribute('style', 'pointer-events:inherit');
            cleanupElement(element);

            // we create the insert command that is stored on the stack
            // undo means to call cmd.unapply(), redo means to call cmd.apply()
            svgCanvas.addCommandToHistory(new InsertElementCommand(element));

            if (svgCanvas.getCurrentMode() === 'path') {
                svgCanvas.pathActions.toEditMode(element);
            } else if (svgCanvas.getCurrentConfig().selectNew) {
                svgCanvas.selectOnly([element], true);
            }

            svgCanvas.call('changed', [element]);
        }, aniDur * 1000);
    }

    eventContext_.setStartTransform(null);
};

export const dblClickEvent = function (evt: MouseEvent) {
    const selectedElements = svgCanvas.getSelectedElements();
    const evtTarget = evt.target as SVGElement;
    const parent = evtTarget.parentNode as SVGElement;

    let mouseTarget = svgCanvas.getMouseTarget(evt);
    const { tagName } = mouseTarget;
    const targetLocked = isElementEffectivelyLocked(mouseTarget);

    if (!targetLocked && tagName === 'text' && svgCanvas.getCurrentMode() !== 'textedit') {
        const pt = transformPoint(evt.pageX, evt.pageY, svgCanvas.getRootSctm());
        svgCanvas.textActions.select(mouseTarget, pt.x, pt.y);
    }

    // Do nothing if already in current group
    if (parent === svgCanvas.getCurrentGroup()) { return; }

    if ((tagName === 'g' || tagName === 'a') && getRotationAngle(mouseTarget)) {
        // TODO: Allow method of in-group editing without having to do
        // this (similar to editing rotated paths)

        // Ungroup and regroup
        svgCanvas.pushGroupProperties(mouseTarget);
        mouseTarget = selectedElements[0];
        svgCanvas.clearSelection(true);
    }
    // Reset context
    if (svgCanvas.getCurrentGroup()) {
        draw.leaveContext();
    }

    if ((parent.tagName !== 'g' && parent.tagName !== 'a') ||
        parent === svgCanvas.getCurrentDrawing().getCurrentLayer() ||
        mouseTarget === svgCanvas.selectorManager.selectorParentGroup
    ) {
        // Escape from in-group edit
        return;
    }
    draw.setContext(mouseTarget);
};

/**
 * Follows these conditions:
 * - When we are in a create mode, the element is added to the canvas but the
 *   action is not recorded until mousing up.
 * - When we are in select mode, select the element, remember the position
 *   and do nothing else.
 * @param {MouseEvent} evt
 * @fires module:svgcanvas.SvgCanvas#event:ext_mouseDown
 * @returns {void}
 */
export const mouseDownEvent = function (evt: MouseEvent) {
    const dataStorage = svgCanvas.getDataStorage();
    const selectedElements = svgCanvas.getSelectedElements();
    const currentZoom = svgCanvas.getCurrentZoom();
    const curShape = svgCanvas.getStyle();
    const { $id } = svgCanvas;
    const currentLayerLocked = isLayerLocked(svgCanvas.getCurrentDrawing().getCurrentLayer());
    if (svgCanvas.inputState.spaceKeyDown || evt.button === 1) {
        return;
    }

    const rightClick = evt.button === 2;

    if (evt.altKey && !currentLayerLocked) { // duplicate when dragging
        svgCanvas.cloneSelectedElements(0, 0);
    }

    const groupElements = document.querySelectorAll("#svgcontent g");
    let firstValidGroupElement: SVGGElement | null;

    for (const el of groupElements) {
        if (el.namespaceURI.endsWith("://www.w3.org/2000/svg")) {
            firstValidGroupElement = el as SVGGElement;
            break;
        }
    }

    if (firstValidGroupElement) {
        svgCanvas.setRootSctm(firstValidGroupElement.getScreenCTM().inverse());
    } else {
        console.warn("Could not find a <g> element on #svgcontent");
    }

    const pt = transformPoint(evt.pageX, evt.pageY, svgCanvas.getRootSctm()),
        mouseX = pt.x * currentZoom,
        mouseY = pt.y * currentZoom;

    evt.preventDefault();

    if (rightClick) {
        svgCanvas.setCurrentMode('select');
        eventContext_.setLastClickPoint(pt);
    }

    let x = mouseX / currentZoom,
        y = mouseY / currentZoom;
    let mouseTarget = svgCanvas.getMouseTarget(evt);

    if (mouseTarget.tagName === 'a' && mouseTarget.childNodes.length === 1) {
        mouseTarget = mouseTarget.firstChild;
    }

    // realX/y ignores grid-snap value
    const realX = x;
    eventContext_.setStartX(x);
    eventContext_.setRStartX(x);
    const realY = y;
    eventContext_.setStartY(y);
    eventContext_.setRStartY(y);

    if (svgCanvas.getCurrentConfig().gridSnapping) {
        x = snapToGrid(x);
        y = snapToGrid(y);
        eventContext_.setStartX(snapToGrid(eventContext_.getStartX()));
        eventContext_.setStartY(snapToGrid(eventContext_.getStartY()));
    }

    // if it is a selector grip, then it must be a single element selected,
    // set the mouseTarget to that and update the mode to rotate/resize

    if (mouseTarget === svgCanvas.selectorManager.selectorParentGroup && !isNullish(selectedElements[0])) {
        const grip = evt.target;
        const griptype = dataStorage.get(grip, 'type');
        // rotating
        if (griptype === 'rotate') {
            svgCanvas.setCurrentMode('rotate');
            // resizing
        } else if (griptype === 'resize') {
            svgCanvas.setCurrentMode('resize');
            eventContext_.setCurrentResizeMode(dataStorage.get(grip, 'dir'));
        }
        mouseTarget = selectedElements[0];
    }

    eventContext_.setStartTransform(mouseTarget.getAttribute('transform'));

    const tlist = getTransformList(mouseTarget);
    const currentMode = svgCanvas.getCurrentMode();
    const targetLocked = isElementEffectivelyLocked(mouseTarget);
    if ((currentLayerLocked || targetLocked) && !['select', 'zoom', 'panning', 'multiselect'].includes(currentMode)) {
        return;
    }
    switch (currentMode) {
        case 'select':
            svgCanvas.inputState.setStarted(true);
            eventContext_.setCurrentResizeMode('none');
            if (rightClick) {
                svgCanvas.inputState.setStarted(false);
            }

            if (mouseTarget !== svgCanvas.getSVGRoot()) {
                // if this element is not yet selected, clear selection and select it
                if (!selectedElements.includes(mouseTarget)) {
                    // only clear selection if shift is not pressed (otherwise, add
                    // element to selection)
                    if (!evt.shiftKey) {
                        // No need to do the call here as it will be done on addToSelection
                        svgCanvas.clearSelection(true);
                    }
                    svgCanvas.addToSelection([mouseTarget]);
                    eventContext_.setJustSelected(mouseTarget);
                    svgCanvas.pathActions.clear();
                }
                // else if it's a path, go into pathedit mode in mouseup

                if (!rightClick) {
                    // insert a dummy transform so if the element(s) are moved it will have
                    // a transform to use for its translate
                    for (const selectedElement of selectedElements) {
                        if (isNullish(selectedElement) || isElementEffectivelyLocked(selectedElement)) { continue; }
                        const slist = getTransformList(selectedElement);
                        if (slist.numberOfItems) {
                            slist.insertItemBefore(svgCanvas.getSVGRoot().createSVGTransform(), 0);
                        } else {
                            slist.appendItem(svgCanvas.getSVGRoot().createSVGTransform());
                        }
                    }
                }
            } else if (!rightClick) {
                svgCanvas.clearSelection();
                svgCanvas.setCurrentMode('multiselect');
                if (isNullish(eventContext_.getRubberBox())) {
                    eventContext_.setRubberBox(svgCanvas.selectorManager.getRubberBandBox());
                }
                eventContext_.setRStartX(eventContext_.getRStartX() * currentZoom);
                eventContext_.setRStartY(eventContext_.getRStartY() * currentZoom);

                assignAttributes(eventContext_.getRubberBox(), {
                    x: eventContext_.getRStartX(),
                    y: eventContext_.getRStartY(),
                    width: 0,
                    height: 0,
                    display: 'inline'
                }, 100);
            }
            break;
        case 'zoom':
            svgCanvas.inputState.setStarted(true);
            if (isNullish(eventContext_.getRubberBox())) {
                eventContext_.setRubberBox(svgCanvas.selectorManager.getRubberBandBox());
            }
            assignAttributes(eventContext_.getRubberBox(), {
                x: realX * currentZoom,
                y: realX * currentZoom,
                width: 0,
                height: 0,
                display: 'inline'
            }, 100);
            break;
        case 'panning':
            svgCanvas.inputState.setStarted(true);
            svgCanvas.editor.setPanning(true);
            break;
        case 'resize': {
            svgCanvas.inputState.setStarted(true);
            eventContext_.setStartX(x);
            eventContext_.setStartY(y);

            // Getting the BBox from the selection box, since we know we
            // want to orient around it
            eventContext_.setInitBbox(utilsGetBBox($id<SVGGraphicsElement>('selectedBox0')));
            const bb = {};
            for (const [key, val] of Object.entries<number>(eventContext_.getInitBbox())) {
                bb[key] = val / currentZoom;
            }
            eventContext_.setInitBbox(bb);

            // append three dummy transforms to the tlist so that
            // we can translate,scale,translate in mousemove
            const pos = getRotationAngle(mouseTarget) ? 1 : 0;

            if (hasMatrixTransform(tlist)) {
                tlist.insertItemBefore(svgCanvas.getSVGRoot().createSVGTransform(), pos);
                tlist.insertItemBefore(svgCanvas.getSVGRoot().createSVGTransform(), pos);
                tlist.insertItemBefore(svgCanvas.getSVGRoot().createSVGTransform(), pos);
            } else {
                tlist.appendItem(svgCanvas.getSVGRoot().createSVGTransform());
                tlist.appendItem(svgCanvas.getSVGRoot().createSVGTransform());
                tlist.appendItem(svgCanvas.getSVGRoot().createSVGTransform());

                if (supportsNonScalingStroke()) {
                    // Handle crash for newer Chrome and Safari 6 (Mobile and Desktop):
                    // https://code.google.com/p/svg-edit/issues/detail?id=904
                    // Chromium issue: https://code.google.com/p/chromium/issues/detail?id=114625
                    // TODO: Remove this workaround once vendor fixes the issue
                    const iswebkit = isWebkit();

                    let delayedStroke;
                    if (iswebkit) {
                        delayedStroke = function (ele) {
                            const stroke_ = ele.getAttribute('stroke');
                            ele.removeAttribute('stroke');
                            // Re-apply stroke after delay. Anything higher than 1 seems to cause flicker
                            if (stroke_ !== null) setTimeout(function () { ele.setAttribute('stroke', stroke_); }, 0);
                        };
                    }
                    mouseTarget.style.vectorEffect = 'non-scaling-stroke';
                    if (iswebkit) { delayedStroke(mouseTarget); }

                    const all = mouseTarget.getElementsByTagName('*'),
                        len = all.length;
                    for (let i = 0; i < len; i++) {
                        if (!all[i].style) { // mathML
                            continue;
                        }
                        all[i].style.vectorEffect = 'non-scaling-stroke';
                        if (iswebkit) { delayedStroke(all[i]); }
                    }
                }
            }
            break;
        }
        case 'fhellipse':
        case 'fhrect':
        case 'fhpath':
            eventContext_.setStart({ x: realX, y: realY });
            eventContext_.setControllPoint1('x', 0);
            eventContext_.setControllPoint1('y', 0);
            eventContext_.setControllPoint2('x', 0);
            eventContext_.setControllPoint2('y', 0);
            svgCanvas.inputState.setStarted(true);
            eventContext_.setDAttr(realX + ',' + realY + ' ');
            // Commented out as doing nothing now:
            // strokeW = parseFloat(curShape.stroke_width) === 0 ? 1 : curShape.stroke_width;
            svgCanvas.addSVGElementFromJson({
                element: 'polyline',
                curStyles: true,
                attr: {
                    points: eventContext_.getDAttr(),
                    id: svgCanvas.getNextId(),
                    fill: 'none',
                    opacity: curShape.opacity / 2,
                    'stroke-linecap': 'round',
                    style: 'pointer-events:none',
                    "pe-fill": "REGISTRATION|100",
                    "pe-stroke": "REGISTRATION|100",
                }
            });
            eventContext_.setFreehand('minx', realX);
            eventContext_.setFreehand('maxx', realX);
            eventContext_.setFreehand('miny', realY);
            eventContext_.setFreehand('maxy', realY);
            break;
        case 'image': {
            svgCanvas.inputState.setStarted(true);
            const newImage = svgCanvas.addSVGElementFromJson({
                element: 'image',
                attr: {
                    x,
                    y,
                    width: 0,
                    height: 0,
                    id: svgCanvas.getNextId(),
                    opacity: curShape.opacity / 2,
                    style: 'pointer-events:inherit'
                }
            });
            setHref(newImage, eventContext_.getLastGoodImgUrl());
            preventClickDefault(newImage);
            break;
        } case 'square':
        // TODO: once we create the rect, we lose information that this was a square
        // (for resizing purposes this could be important)
        // Fallthrough
        case 'rect':
            svgCanvas.inputState.setStarted(true);
            eventContext_.setStartX(x);
            eventContext_.setStartY(y);
            svgCanvas.addSVGElementFromJson({
                element: 'rect',
                curStyles: true,
                attr: {
                    x,
                    y,
                    width: 0,
                    height: 0,
                    id: svgCanvas.getNextId(),
                    opacity: curShape.opacity / 2,
                    "pe-fill": "REGISTRATION|100",
                    "pe-stroke": "REGISTRATION|100",
                }
            });
            break;
        case 'line': {
            svgCanvas.inputState.setStarted(true);
            const strokeW = Number(curShape.stroke_width) === 0 ? 1 : curShape.stroke_width;
            svgCanvas.addSVGElementFromJson({
                element: 'line',
                curStyles: true,
                attr: {
                    x1: x,
                    y1: y,
                    x2: x,
                    y2: y,
                    id: svgCanvas.getNextId(),
                    stroke: curShape.stroke,
                    'stroke-width': strokeW,
                    'stroke-dasharray': curShape.stroke_dasharray,
                    'stroke-linejoin': curShape.stroke_linejoin,
                    'stroke-linecap': curShape.stroke_linecap,
                    'stroke-opacity': curShape.stroke_opacity,
                    fill: 'none',
                    opacity: curShape.opacity / 2,
                    style: 'pointer-events:none',
                    "pe-fill": "REGISTRATION|100",
                    "pe-stroke": "REGISTRATION|100",
                }
            });
            break;
        } case 'circle':
            svgCanvas.inputState.setStarted(true);
            svgCanvas.addSVGElementFromJson({
                element: 'circle',
                curStyles: true,
                attr: {
                    cx: x,
                    cy: y,
                    r: 0,
                    id: svgCanvas.getNextId(),
                    opacity: curShape.opacity / 2,
                    "pe-fill": "REGISTRATION|100",
                    "pe-stroke": "REGISTRATION|100",
                }
            });
            break;
        case 'ellipse':
            svgCanvas.inputState.setStarted(true);
            svgCanvas.addSVGElementFromJson({
                element: 'ellipse',
                curStyles: true,
                attr: {
                    cx: x,
                    cy: y,
                    rx: 0,
                    ry: 0,
                    id: svgCanvas.getNextId(),
                    opacity: curShape.opacity / 2,
                    "pe-fill": "REGISTRATION|100",
                    "pe-stroke": "REGISTRATION|100",
                }
            });
            break;
        case 'text':
        case 'textarea':
            svgCanvas.inputState.setStarted(true);
            const newText = svgCanvas.addSVGElementFromJson({
                element: 'text',
                curStyles: true,
                attr: {
                    id: svgCanvas.getNextId(),
                    fill: eventContext_.getCurText('fill'),
                    'stroke-width': 0,
                    'font-size': eventContext_.getCurText('font_size'),
                    'font-family': eventContext_.getCurText('font_family'),
                    'text-anchor': 'start',
                    'xml:space': 'preserve',
                    opacity: curShape.opacity,
                    'pe-fill': 'REGISTRATION|100',
                    'pe-stroke': 'REGISTRATION|100',
                }
            });
            if (zenDataServiceInstance.GetDesignerMode() === "artwork") {
                newText.textContent = 'text';
                newText.setAttribute('transform', `translate(${x}, ${y})`);
            } else {
                newText.setAttribute('x', x.toString());
                newText.setAttribute('y', y.toString());
            }
            break;
        case 'path':
        // Fall through
        case 'pathedit':
            eventContext_.setStartX(eventContext_.getStartX() * currentZoom);
            eventContext_.setStartY(eventContext_.getStartY() * currentZoom);
            svgCanvas.pathActions.mouseDown(evt, mouseTarget, eventContext_.getStartX(), eventContext_.getStartY());
            svgCanvas.inputState.setStarted(true);
            break;
        case 'textedit':
            eventContext_.setStartX(eventContext_.getStartX() * currentZoom);
            eventContext_.setStartY(eventContext_.getStartY() * currentZoom);
            svgCanvas.textActions.mouseDown(evt, mouseTarget, eventContext_.getStartX(), eventContext_.getStartY());
            svgCanvas.inputState.setStarted(true);
            break;
        case 'rotate':
            svgCanvas.inputState.setStarted(true);
            // we are starting an undoable change (a drag-rotation)
            svgCanvas.undoMgr.beginUndoableChange('transform', selectedElements);
            break;
        default:
            // This could occur in an extension
            break;
    }

    /**
  * The main (left) mouse button is held down on the canvas area.
  * @event module:svgcanvas.SvgCanvas#event:ext_mouseDown
  * @type {PlainObject}
  * @property {MouseEvent} event The event object
  * @property {Float} start_x x coordinate on canvas
  * @property {Float} start_y y coordinate on canvas
  * @property {Element[]} selectedElements An array of the selected Elements
  */
    let extResult = svgCanvas.runExtensions('mouseDown', {
        event: evt,
        start_x: eventContext_.getStartX(),
        start_y: eventContext_.getStartY(),
        selectedElements
    }, true);

    if (!Array.isArray(extResult)) {
        extResult = [extResult];
    }

    extResult.forEach(function (r) {
        if (r && r.started) {
            svgCanvas.inputState.setStarted(true);
        }
    });
};
/**
 * @param {Event} e
 * @fires module:event.SvgCanvas#event:updateCanvas
 * @fires module:event.SvgCanvas#event:zoomDone
 * @returns {void}
 */
export const DOMMouseScrollEvent = function (e: JQuery.ScrollEvent) {
    const currentZoom = svgCanvas.getCurrentZoom();
    const { $id } = svgCanvas;
    if (!e.shiftKey) { return; }

    e.preventDefault();
    const evt = e.originalEvent as WheelEvent & { wheelDelta: number };

    const groupElements = document.querySelectorAll("#svgcontent g");
    let firstValidGroupElement: SVGGElement | null;

    for (const el of groupElements) {
        if (el.namespaceURI.endsWith("://www.w3.org/2000/svg")) {
            firstValidGroupElement = el as SVGGElement;
            break;
        }
    }

    if (firstValidGroupElement) {
        svgCanvas.setRootSctm(firstValidGroupElement.getScreenCTM().inverse());
    } else {
        console.warn("Could not find a <g> element on #svgcontent");
    }

    const workarea = document.getElementById('workarea');
    const scrbar = 15;
    const rulerwidth = svgCanvas.getCurrentConfig().showRulers ? 16 : 0;

    // mouse relative to content area in content pixels
    const pt = transformPoint(evt.pageX, evt.pageY, svgCanvas.getRootSctm());

    // full work area width in screen pixels
    const editorFullW = parseFloat(getComputedStyle(workarea, null).width.replace("px", ""));
    const editorFullH = parseFloat(getComputedStyle(workarea, null).height.replace("px", ""));

    // work area width minus scroll and ruler in screen pixels
    const editorW = editorFullW - scrbar - rulerwidth;
    const editorH = editorFullH - scrbar - rulerwidth;

    // work area width in content pixels
    const workareaViewW = editorW * svgCanvas.getRootSctm().a;
    const workareaViewH = editorH * svgCanvas.getRootSctm().d;

    // content offset from canvas in screen pixels
    const wOffset = findPos(workarea);
    const wOffsetLeft = wOffset.left + rulerwidth;
    const wOffsetTop = wOffset.top + rulerwidth;

    const delta = evt.wheelDelta ?? evt.detail ?? 0;
    if (!delta) {
        return;
    }

    let factor = Math.max(3 / 4, Math.min(4 / 3, (delta)));

    let wZoom, hZoom;
    if (factor > 1) {
        wZoom = Math.ceil(editorW / workareaViewW * factor * 100) / 100;
        hZoom = Math.ceil(editorH / workareaViewH * factor * 100) / 100;
    } else {
        wZoom = Math.floor(editorW / workareaViewW * factor * 100) / 100;
        hZoom = Math.floor(editorH / workareaViewH * factor * 100) / 100;
    }
    let zoomlevel = Math.min(wZoom, hZoom);
    zoomlevel = Math.min(10, Math.max(0.01, zoomlevel));
    if (zoomlevel === currentZoom) {
        return;
    }
    factor = zoomlevel / currentZoom;

    // top left of workarea in content pixels before zoom
    const topLeftOld = transformPoint(wOffsetLeft, wOffsetTop, svgCanvas.getRootSctm());

    // top left of workarea in content pixels after zoom
    const topLeftNew = {
        x: pt.x - (pt.x - topLeftOld.x) / factor,
        y: pt.y - (pt.y - topLeftOld.y) / factor
    };

    // top left of workarea in canvas pixels relative to content after zoom
    const topLeftNewCanvas = {
        x: topLeftNew.x * zoomlevel,
        y: topLeftNew.y * zoomlevel
    };

    // new center in canvas pixels
    const newCtr = {
        x: topLeftNewCanvas.x - rulerwidth + editorFullW / 2,
        y: topLeftNewCanvas.y - rulerwidth + editorFullH / 2
    };

    svgCanvas.setZoom(zoomlevel);

    svgCanvas.call('updateCanvas', { center: false, newCtr });
    svgCanvas.call('zoomDone');
};
